import React, { FC, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import Copy from 'Src/components/Copy';
import { Flex } from 'Src/components/layout/Flex';
import { getTypedFormComponents } from 'Src/feature/form/getTypedFormComponents';
import { useForm } from 'Src/feature/form/useForm';
import { OrganizationOption } from 'Src/feature/provision/provisionService';

import { ProvisionItemCta } from './ProvisionItemCTA';
import { ProvisionItemList } from './ProvisionItemList';
import { useEmailWorkflow } from './useEmailWorkflow';
import { useOrganization } from './useOrganization';

import styles from './OrgSelect.module.scss';

class FormModel {
  search = '';
  constructor(part?: Partial<FormModel>) {
    if (part) Object.assign(this, part);
  }
}

const { Form, Field } = getTypedFormComponents<FormModel>();

const OrgSelector: FC = () => {
  const { t } = useTranslation();

  const workflow = useEmailWorkflow();
  const organizationGetAll = useOrganization.query.getAll();

  const form = useForm<FormModel>({ defaultValues: { ...new FormModel() } });
  const { search } = form.state.watch();

  const options = { organizations: [] as OrganizationOption[] };
  if (!search || search.length === 0) {
    options.organizations = workflow.data.organizations;
  } else {
    options.organizations = workflow.data.organizations.filter((l) => JSON.stringify(l).toLowerCase().includes(search.toLowerCase()));
  }

  const selectOrg = useCallback((organizationId: number) => {
    workflow.updateData({ organizationId });
    workflow.steps.canGoNext = true;
  }, [workflow]);

  useEffect(() => {
    if (workflow.data.organizations.length > 0) return;
    if (organizationGetAll.isLoading) return;
    if (!organizationGetAll.data || !organizationGetAll.data.length) return;

    workflow.updateData({ organizations: organizationGetAll.data });

    if (organizationGetAll.data.length === 1) {
      selectOrg(organizationGetAll.data[0].id);
      workflow.steps.goNext();
    }
    // only want to update when organization data changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflow.data.organizations, organizationGetAll.data, organizationGetAll.isLoading]);

  useEffect(() => {
    workflow.steps.canGoNext = !!workflow.data.organizationId;
    // only update when "organizationId" changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflow.data.organizationId]);

  if (organizationGetAll.isLoading) return null;

  return (
    <Flex className={styles.root} alignItems="center" fullHeight>
      <Copy variant="h6" value={t('provision.labels.greeting', { name: workflow.data.user?.firstName })} />
      <Copy variant="h6" color="secondary" value={t('provision.labels.orgSelect')} />
      <Form usedForm={form} className={styles.searchFieldWrapper}>
        <Field
          type="search"
          name="search"
          id="org-select-search"
          placeholder={t('organization.selector.search')}
        />
      </Form>

      <ProvisionItemList
        title={t('organization.label')}
      >
        {options.organizations.map((org) => (
          <ProvisionItemCta
            data-testid="org-item"
            key={org.id}
            selected={workflow.data.organizationId === org.id}
            onClick={() => selectOrg(org.id)}
            title={org.name}
          />
        ))}
      </ProvisionItemList>
    </Flex>
  );
};

export default OrgSelector;
